import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, adminCreateFlight } from "../../lib/adminApi";
import BottleSilhouette from "../../components/BottleSilhouette";

export default function AdminFlights() {
  const [flights, setFlights] = useState({ static: [], admin: [] });
  const [allGins, setAllGins] = useState([]);
  const [building, setBuilding] = useState(false);
  const [form, setForm] = useState({
    name: "",
    kicker: "Custom Series · 01",
    blurb: "",
    bartender: "BENO",
    color_key: "luxury",
    gin_ids: [],
  });
  const [search, setSearch] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/flights").then((r) => setFlights(r.data));
    adminApi.get("/admin/gins").then((r) => setAllGins(r.data.gins));
  }, []);

  const toggleGin = (id) => setForm((f) => {
    const cur = f.gin_ids;
    if (cur.includes(id)) return { ...f, gin_ids: cur.filter((x) => x !== id) };
    if (cur.length >= 3) return f;
    const g = allGins.find((x) => x.id === id);
    if (g && (g.availability_status === "NOT_AVAILABLE" || g.availability_status === "HIDDEN")) {
      alert(`${g.name} is ${g.availability_status === "HIDDEN" ? "hidden" : "not available"}. Flights can only contain available gins.`);
      return f;
    }
    return { ...f, gin_ids: [...cur, id] };
  });

  const submit = async () => {
    if (!form.name || !form.blurb || form.gin_ids.length < 2) return;
    try {
      await adminCreateFlight(form);
      setSavedAt(new Date().toLocaleTimeString());
      setForm({ ...form, name: "", blurb: "", gin_ids: [] });
      const r = await adminApi.get("/admin/flights"); setFlights(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not publish flight");
    }
  };

  const filteredGins = allGins.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="px-5 md:px-10 py-8 max-w-7xl" data-testid="admin-flights">
      <header className="mb-7">
        <p className="overline text-[#C17F59] mb-2">Flights · curated journeys</p>
        <h1 className="font-display text-4xl">Build a flight. Tell a story.</h1>
        <p className="text-sm text-white/55 mt-2 max-w-2xl">12 default flights ship with the Atlas. Build new ones from any 2–3 gins on the menu.</p>
      </header>

      {/* Builder */}
      <section className="bg-[#15171A] border border-white/8 p-6 mb-10" data-testid="flight-builder">
        <p className="overline text-[#C17F59] mb-3">Flight Builder</p>
        <button onClick={() => setBuilding((b) => !b)} className="text-xs uppercase tracking-[0.22em] text-[#C17F59] hover:text-[#D89A74] mb-4" data-testid="toggle-builder">
          {building ? "Cancel" : "+ New flight"}
        </button>

        {building && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="flight-name" />
              <Field label="Kicker (subtitle)" value={form.kicker} onChange={(v) => setForm({ ...form, kicker: v })} testid="flight-kicker" />
              <Textarea label="Blurb" value={form.blurb} onChange={(v) => setForm({ ...form, blurb: v })} testid="flight-blurb" />
              <Field label="Bartender" value={form.bartender} onChange={(v) => setForm({ ...form, bartender: v })} testid="flight-bartender" />
              <SelectField label="Color theme" value={form.color_key} options={["luxury", "classic", "citrus", "floral", "spice", "coastal", "herbal", "fruity", "earthy", "experimental"]} onChange={(v) => setForm({ ...form, color_key: v })} testid="flight-color" />
              <p className="text-xs text-white/50">Selected: {form.gin_ids.length}/3</p>
              <button onClick={submit} disabled={!form.name || !form.blurb || form.gin_ids.length < 2}
                      className="bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                      data-testid="flight-submit">
                Save flight
              </button>
              {savedAt && <p className="text-[10px] uppercase tracking-[0.25em] text-green-400 mt-2">Saved · {savedAt}</p>}
            </div>
            <div className="border border-white/5 p-3 max-h-[420px] overflow-y-auto">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter gins..."
                     className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none mb-2" />
              {filteredGins.slice(0, 60).map((g) => {
                const sel = form.gin_ids.includes(g.id);
                const unavail = g.availability_status === "NOT_AVAILABLE" || g.availability_status === "HIDDEN";
                return (
                  <button key={g.id} onClick={() => toggleGin(g.id)} disabled={unavail}
                          className={`w-full flex items-center gap-3 p-2 text-left transition-colors ${
                            sel ? "bg-[#C17F59]/15 border-l-2 border-[#C17F59]" : "hover:bg-white/3"
                          } ${unavail ? "opacity-40 cursor-not-allowed" : ""}`}
                          data-testid={`pick-gin-${g.id}`}>
                    <BottleSilhouette category={g.category} size={28} />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base truncate">{g.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                        {g.country} · ₹{g.price}
                        {unavail && <span className="ml-2 text-amber-400">· {g.availability_status === "HIDDEN" ? "hidden" : "not available"}</span>}
                      </p>
                    </div>
                    {sel && <span className="text-[#C17F59] text-xs">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="space-y-8">
        <Group label="Default Flights" items={flights.static} />
        <Group label="Admin-Built Flights" items={flights.admin} admin />
      </section>
    </div>
  );
}

function Group({ label, items, admin }) {
  return (
    <div>
      <p className="overline text-[#C17F59] mb-3">{label} · {items.length}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((f) => (
          <Link key={f.id} to={`/gin/flights/${f.id}`} target="_blank" rel="noreferrer"
                className={`bg-[#15171A] border p-5 transition-colors ${
                  f.pourable === false ? "border-amber-700/40 hover:border-amber-500/60" : "border-white/8 hover:border-white/25"
                }`}
                data-testid={`admin-flight-${f.id}`}>
            <p className="overline text-[#C17F59] mb-1">{f.kicker}</p>
            <h3 className="font-display text-2xl mb-2 leading-tight">{f.name}</h3>
            <p className="text-sm text-white/55 mb-4 line-clamp-2">{f.blurb}</p>
            {f.warnings && f.warnings.length > 0 && (
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-400 mb-2" data-testid={`flight-warning-${f.id}`}>
                ⚠ {f.warnings[0]}
              </p>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/40">{f.pours} · ₹{f.price}</span>
              <span className={`uppercase tracking-[0.2em] ${f.pourable === false ? "text-amber-400" : "text-[#C17F59]"}`}>
                {f.pourable === false ? "paused" : (admin ? "admin" : "default")}
              </span>
            </div>
          </Link>
        ))}
        {items.length === 0 && <p className="text-white/40 text-sm">Nothing here yet.</p>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
             className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none transition-colors"
             data-testid={testid} />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full bg-[#101113] text-white border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
              data-testid={testid}>
        {options.map((o) => <option key={o} className="bg-[#15171A] text-white">{o}</option>)}
      </select>
    </label>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent text-white border border-white/10 focus:border-[#C17F59] p-2 text-sm focus:outline-none resize-y"
                data-testid={testid} />
    </label>
  );
}
